import React, { useState, useEffect } from 'react';
import { ContentProvider, useContent } from './context/ContentContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { BottomNav, NavTabId } from './components/common/BottomNav';
import { Navbar } from './components/public/Navbar';
import { HeroSection } from './components/public/HeroSection';
import { ChapterSection } from './components/public/ChapterSection';
import { MobileTruckSection } from './components/public/MobileTruckSection';
import { OpenBoothSection } from './components/public/OpenBoothSection';
import { MenuSection } from './components/public/MenuSection';
import { BaristasSection } from './components/public/BaristasSection';
import { ContactSection } from './components/public/ContactSection';
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminLogin } from './components/admin/AdminLogin';
import { CourierPortal } from './components/admin/CourierPortal';
import { PageSkeletonLoader } from './components/public/PageSkeletonLoader';
import { OrderingSystemModal } from './components/public/ordering/OrderingSystemModal';
import { OutletSelector } from './components/public/ordering/OutletSelector';
import { PwaInstallEntry, isPwaInstallUrl } from './components/public/PwaInstallEntry';
import { PwaNotificationPrompt } from './components/public/PwaNotificationPrompt';
import { PwaOnboarding } from './components/public/PwaOnboarding';
import CustomerProfileTab from './components/public/ordering/CustomerProfileTab';
import CustomerAuthForm from './components/public/ordering/CustomerAuthForm';
import { PrivacyPolicyPage } from './components/public/PrivacyPolicyPage';
import { isPwaStandalone, isOnboardingCompleted, setOnboardingCompleted } from './utils/pwaDetection';
import { MessageCircle, ShoppingBag, Lock, User, RefreshCw, Receipt, Search, Store, Check, RotateCcw, Clock, ArrowLeft } from 'lucide-react';
import { createWhatsAppLink, formatOrderDateTime, formatRupiah } from './utils/formatters';
import { motion, AnimatePresence } from 'motion/react';
import { MenuItem, CustomerProfile, CustomerOrder, OrderType } from './types';
import { getSupabase, getCurrentCustomerProfile, logoutCustomer, getCustomerOrdersRpc } from './utils/supabase';
import { extractAndPersistReferralCode } from './utils/supabaseReferral';
import { resolvePersistedOrderServiceFee } from './utils/deliveryFee';

function getValidOutletParamFromUrl(): 'sudirman' | 'kelakap_7' | null {
  if (typeof window === 'undefined') return null;
  try {
    const searchParams = new URLSearchParams(window.location.search);
    let raw = searchParams.get('outlet');
    if (!raw && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.split('?')[1] || '';
      raw = new URLSearchParams(hashQuery).get('outlet');
    }
    if (!raw) return null;
    const normalized = raw.trim().toLowerCase();
    if (normalized === 'sudirman') return 'sudirman';
    if (normalized === 'kelakap_7') return 'kelakap_7';
    return null;
  } catch {
    return null;
  }
}

const AppContent: React.FC = () => {
  const { data, auth, isLoading, isDataReady, completeLoading } = useContent();

  const [isStandaloneMode] = useState<boolean>(() => isPwaStandalone());

  const [deepLinkOutletId, setDeepLinkOutletId] = useState<'sudirman' | 'kelakap_7' | null>(() =>
    getValidOutletParamFromUrl()
  );

  useEffect(() => {
    extractAndPersistReferralCode();
  }, []);

  const isPrivacyUrl = (pathname: string, hash: string): boolean => {
    const cleanPath = (pathname || '').toLowerCase();
    const cleanHash = (hash || '').toLowerCase();
    return (
      cleanPath === '/privacy-policy' ||
      cleanPath.startsWith('/privacy-policy') ||
      cleanPath === '/privacy' ||
      cleanPath.startsWith('/privacy') ||
      cleanPath === '/kebijakan-privasi' ||
      cleanPath.startsWith('/kebijakan-privasi') ||
      cleanHash === '#privacy-policy' ||
      cleanHash.startsWith('#/privacy-policy') ||
      cleanHash.startsWith('#privacy-policy') ||
      cleanHash === '#kebijakan-privasi' ||
      cleanHash.startsWith('#/kebijakan-privasi')
    );
  };

  const [isPrivacyRoute, setIsPrivacyRoute] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return isPrivacyUrl(window.location.pathname || '', window.location.hash || '');
    } catch {
      return false;
    }
  });

  const [isAdminRoute, setIsAdminRoute] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;

    try {
      const pathname = window.location.pathname || '';
      const hash = window.location.hash || '';

      const isUrlAdmin =
        pathname.startsWith('/admin') ||
        pathname.startsWith('/courier') ||
        pathname.startsWith('/kurir') ||
        hash.startsWith('#/admin') ||
        hash.startsWith('#admin') ||
        hash.startsWith('#/courier') ||
        hash.startsWith('#courier') ||
        hash.startsWith('#/kurir') ||
        hash.startsWith('#kurir');

      return isUrlAdmin;
    } catch (err) {
      console.warn('[App] Initial route detection handled:', err);
      return false;
    }
  });

  // Ensure initial HTML loading overlay is dismissed once React is ready and PageSkeletonLoader is not active
  useEffect(() => {
    if (isStandaloneMode && isLoading && !isPrivacyRoute) {
      return;
    }
    if (isDataReady || isAdminRoute || isPrivacyRoute) {
      const rafId = requestAnimationFrame(() => {
        try {
          const globalDismiss = (window as unknown as { __dismissInitialLoadingOverlay?: (imm?: boolean) => void })
            .__dismissInitialLoadingOverlay;
          if (typeof globalDismiss === 'function') {
            globalDismiss(false);
          } else {
            const el = document.getElementById('initial-loading-overlay');
            if (el && el.parentNode) {
              el.parentNode.removeChild(el);
            }
          }
        } catch {}
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isStandaloneMode, isLoading, isDataReady, isAdminRoute, isPrivacyRoute]);

  const [isOrderingOpen, setIsOrderingOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (getValidOutletParamFromUrl()) {
        return true;
      }
      const hash = window.location.hash || '';
      const pathname = window.location.pathname || '';
      const search = window.location.search || '';
      return (
        hash === '#order' ||
        hash === '#/order' ||
        hash.startsWith('#order?') ||
        hash.startsWith('#/order?') ||
        search.includes('source=dana_return') ||
        pathname.startsWith('/order')
      );
    } catch {
      return false;
    }
  });
  const [isMemberOnlyFlow, setIsMemberOnlyFlow] = useState<boolean>(false);
  const [orderingMenuItem, setOrderingMenuItem] = useState<MenuItem | null>(null);
  const [orderingInitialType, setOrderingInitialType] = useState<OrderType>('TAKE AWAY');
  const [activeTab, setActiveTab] = useState<NavTabId>('home');
  const [tabHistory, setTabHistory] = useState<NavTabId[]>([]);
  const [visitedTabs, setVisitedTabs] = useState<Set<NavTabId>>(() => new Set<NavTabId>(['home']));
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [loadingCustomerProfile, setLoadingCustomerProfile] = useState<boolean>(true);

  const [showOnboarding, setShowOnboarding] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return isPwaStandalone() && !isOnboardingCompleted();
  });
  const [onboardingTargetTab, setOnboardingTargetTab] = useState<NavTabId | null>(null);

  const handleTabChange = (tab: NavTabId) => {
    if (!customerProfile && (tab === 'pesanan' || tab === 'akun')) {
      if (isStandaloneMode) {
        setOnboardingTargetTab(tab);
        setShowOnboarding(true);
        return;
      }
    }
    if (tab === 'home') {
      setTabHistory([]);
    } else if (tab !== activeTab) {
      setTabHistory((prev) => [...prev.filter((t) => t !== activeTab && t !== tab), activeTab]);
    }
    setVisitedTabs((prev) => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
    setActiveTab(tab);
  };

  const handleBackTab = () => {
    const previousTab = tabHistory.length > 0 ? tabHistory[tabHistory.length - 1] : 'home';
    const nextHistory = previousTab === 'home' ? [] : tabHistory.slice(0, -1);
    setTabHistory(nextHistory);
    setVisitedTabs((visited) => {
      if (visited.has(previousTab)) return visited;
      const nextVisited = new Set(visited);
      nextVisited.add(previousTab);
      return nextVisited;
    });
    setActiveTab(previousTab);
  };

  // Redirect to Home if they successfully log in during onboarding
  useEffect(() => {
    if (customerProfile && onboardingTargetTab) {
      setOnboardingCompleted();
      setOnboardingTargetTab(null);
      setIsOrderingOpen(false);
      setIsMemberOnlyFlow(false);
      setVisitedTabs((prev) => {
        if (prev.has('home')) return prev;
        const next = new Set(prev);
        next.add('home');
        return next;
      });
      setActiveTab('home');
    }
  }, [customerProfile, onboardingTargetTab]);

  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState<boolean>(false);
  const [ordersSearchQuery, setOrdersSearchQuery] = useState<string>('');
  const [ordersExpandedId, setOrdersExpandedId] = useState<string | null>(null);
  const [ordersTabFilter, setOrdersTabFilter] = useState<'past' | 'active'>('past');

  const [isPwaInstallOpen, setIsPwaInstallOpen] = useState<boolean>(() => {
    if (isPwaInstallUrl()) return true;
    if (isPwaStandalone()) return false;
    try {
      const pathname = window.location.pathname || '';
      const hash = window.location.hash || '';
      if (
        pathname.startsWith('/admin') ||
        pathname.startsWith('/courier') ||
        pathname.startsWith('/kurir') ||
        hash.startsWith('#/admin') ||
        hash.startsWith('#admin') ||
        hash.startsWith('#/courier') ||
        hash.startsWith('#courier') ||
        hash.startsWith('#/kurir') ||
        hash.startsWith('#kurir')
      ) {
        return false;
      }
    } catch {}
    try {
      if (sessionStorage.getItem('leton_pwa_landing_dismissed') === 'true') {
        return false;
      }
    } catch {}
    return true;
  });

  const isPesananVisited = visitedTabs.has('pesanan');
  const customerIdKey = customerProfile?.id || customerProfile?.userId || '';

  useEffect(() => {
    if (!customerIdKey) {
      setOrders([]);
      return;
    }
    if (customerIdKey && isPesananVisited) {
      let isMounted = true;
      const loadOrders = async () => {
        setLoadingOrders((prev) => (orders.length === 0 ? true : prev));
        try {
          const rpcResult = await getCustomerOrdersRpc();
          if (isMounted && rpcResult.success && Array.isArray(rpcResult.orders)) {
            const mappedOrders: CustomerOrder[] = rpcResult.orders.map((o: any) => ({
              id: o.id,
              orderNumber: o.order_number || o.orderNumber || 'LTN-????',
              outletId: o.outlet_id || o.outletId || '',
              outletName: o.outlet_name || o.outletName || '',
              customerName: o.customer_name || o.customerName || '',
              customerPhone: o.customer_phone || o.customerPhone || '',
              customerId: o.customer_id || o.customerId,
              userId: o.user_id || o.userId,
              orderType: o.order_type || o.orderType || 'DINE IN',
              tableNumber: o.table_number || o.tableNumber || '',
              items: Array.isArray(o.items) ? o.items : [],
              totalAmount: Number(o.total_amount || o.totalAmount || 0),
              paymentMethod: o.payment_method || o.paymentMethod || 'QRIS',
              paymentStatus: o.payment_status || o.paymentStatus || 'WAITING PAYMENT',
              paymentReceiptUrl: o.payment_receipt_url || o.paymentReceiptUrl,
              paymentReceiptPath: o.payment_receipt_path || o.paymentReceiptPath,
              paymentProofPath: o.payment_proof_path || o.paymentProofPath,
              rejectionReason: o.rejection_reason || o.rejectionReason,
              orderStatus: o.order_status || o.orderStatus || 'NEW',
              customerNote: o.customer_note || o.customerNote || '',
              createdAt: o.created_at || o.createdAt,
              updatedAt: o.updated_at || o.updatedAt,
            }));
            setOrders(mappedOrders);
          }
        } catch (err) {
          console.error('Exception loading orders in App.tsx:', err);
        } finally {
          if (isMounted) {
            setLoadingOrders(false);
          }
        }
      };
      loadOrders();
      return () => {
        isMounted = false;
      };
    }
  }, [customerIdKey, isPesananVisited]);

  // Sync customer profile session on load
  useEffect(() => {
    const applyProfileIfChanged = (profile: CustomerProfile | null) => {
      setCustomerProfile((prev) => {
        if (!profile) return null;
        const prevId = prev?.id || prev?.userId || '';
        const nextId = profile.id || profile.userId || '';
        if (prev && prevId && prevId === nextId) {
          return prev;
        }
        return profile;
      });
      if (profile) {
        if (isStandaloneMode) {
          setOnboardingCompleted();
        }
        setShowOnboarding(false);
      }
    };

    const syncCustomer = async () => {
      try {
        const profile = await getCurrentCustomerProfile();
        applyProfileIfChanged(profile);
      } catch (err) {
        setCustomerProfile(null);
      } finally {
        setLoadingCustomerProfile(false);
      }
    };
    syncCustomer();
  }, []);

  // Sync customer profile while OrderingSystemModal is open or when it closes
  useEffect(() => {
    let isMounted = true;
    const checkProfile = async () => {
      try {
        const profile = await getCurrentCustomerProfile();
        if (!isMounted) return;
        setCustomerProfile((prev) => {
          if (!profile) return null;
          const prevId = prev?.id || prev?.userId || '';
          const nextId = profile.id || profile.userId || '';
          if (
            prev &&
            prevId &&
            prevId === nextId &&
            prev.namaLengkap === profile.namaLengkap &&
            prev.pointsBalance === profile.pointsBalance
          ) {
            return prev;
          }
          return profile;
        });
        if (profile) {
          if (isStandaloneMode) {
            setOnboardingCompleted();
          }
          setShowOnboarding(false);
        }
      } catch {}
    };

    checkProfile();
    if (!isOrderingOpen || customerIdKey) {
      return () => {
        isMounted = false;
      };
    }

    const intervalId = setInterval(checkProfile, 600);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [isOrderingOpen, customerIdKey]);

  // Immediately bypass homepage splash screen when in normal browser or when entering via valid QR outlet deep-link or PWA install QR
  useEffect(() => {
    if ((!isStandaloneMode || deepLinkOutletId || isPwaInstallOpen) && isLoading) {
      completeLoading();
    }
  }, [isStandaloneMode, deepLinkOutletId, isPwaInstallOpen, isLoading, completeLoading]);

  // Sync document title and keep unified single PWA manifest
  useEffect(() => {
    try {
      if (isPrivacyRoute) {
        document.title = 'Kebijakan Privasi — LetonKu';
      } else if (isAdminRoute) {
        document.title =
          auth.isAuthenticated && auth.role === 'courier'
            ? 'LetonKu — Portal Kurir'
            : 'LetonKu — Dashboard Admin';
      } else {
        document.title = 'LetonKu — Website Profil & Online Ordering';
      }
    } catch (err) {
      console.warn('[App] Title update handled:', err);
    }
  }, [isPrivacyRoute, isAdminRoute, auth.isAuthenticated, auth.role]);

  // Listen to popstate / hash change
  useEffect(() => {
    const handleLocationChange = () => {
      try {
        const pathname = window.location.pathname || '';
        const hash = window.location.hash || '';

        const isPrivacy = isPrivacyUrl(pathname, hash);
        setIsPrivacyRoute(isPrivacy);
        if (isPrivacy) {
          setIsAdminRoute(false);
          setIsPwaInstallOpen(false);
          setIsOrderingOpen(false);
          return;
        }

        const isPathAdmin =
          pathname.startsWith('/admin') ||
          pathname.startsWith('/courier') ||
          pathname.startsWith('/kurir') ||
          hash.startsWith('#/admin') ||
          hash.startsWith('#admin') ||
          hash.startsWith('#/courier') ||
          hash.startsWith('#courier') ||
          hash.startsWith('#/kurir') ||
          hash.startsWith('#kurir');
        
        setIsAdminRoute(isPathAdmin);
        setIsPwaInstallOpen(isPwaInstallUrl());

        const validOutletParam = getValidOutletParamFromUrl();
        if (validOutletParam) {
          setDeepLinkOutletId(validOutletParam);
          setIsMemberOnlyFlow(false);
          setIsOrderingOpen(true);
          return;
        }

        const search = window.location.search || '';
        const isOrder =
          hash === '#order' ||
          hash === '#/order' ||
          hash.startsWith('#order?') ||
          hash.startsWith('#/order?') ||
          search.includes('source=dana_return') ||
          pathname.startsWith('/order');
        if (isOrder) {
          setIsOrderingOpen(true);
        }
      } catch (err) {
        console.warn('[App] Location change handled:', err);
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []);

  const closePwaInstall = () => {
    setIsPwaInstallOpen(false);
    try {
      sessionStorage.setItem('leton_pwa_landing_dismissed', 'true');
    } catch {}
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('install');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch {}
  };

  const continueToWebsite = () => {
    closePwaInstall();
    openOrdering();
  };

  const openAdmin = () => {
    setIsAdminRoute(true);
    try { window.history.pushState(null, '', '#admin'); } catch {}
  };

  const closeAdmin = () => {
    setIsAdminRoute(false);
    try { window.history.pushState(null, '', '/#home'); } catch {}
  };

  const openMember = () => {
    setIsMemberOnlyFlow(true);
    setIsOrderingOpen(true);
  };

  const openOrdering = async (item?: MenuItem, initialType?: OrderType) => {
    setIsMemberOnlyFlow(false);
    setOrderingMenuItem(item || null);
    setOrderingInitialType(initialType || 'TAKE AWAY');
    try {
      const client = getSupabase();
      await client.auth.getSession();
    } catch (err) {
      console.warn('Error checking session in openOrdering:', err);
    }
    setIsOrderingOpen(true);
  };

  const closeOrdering = () => {
    const wasOnboardingFlow = Boolean(onboardingTargetTab);
    setIsOrderingOpen(false);
    setIsMemberOnlyFlow(false);
    setOrderingMenuItem(null);
    setOnboardingTargetTab(null);
    setDeepLinkOutletId(null);
    setOrderingInitialType('TAKE AWAY');
    if (wasOnboardingFlow && !customerProfile && isStandaloneMode && !isOnboardingCompleted()) {
      setShowOnboarding(true);
    }
    try {
      const hash = window.location.hash || '';
      const search = window.location.search || '';
      const hasOutletParam = search.includes('outlet=') || hash.includes('outlet=');
      if (
        hash === '#order' ||
        hash === '#/order' ||
        hash.startsWith('#order?') ||
        hash.startsWith('#/order?') ||
        search.includes('source=dana_return') ||
        window.location.pathname.startsWith('/order') ||
        hasOutletParam
      ) {
        window.history.replaceState(null, '', '/#home');
      }
    } catch {}
  };

  // Chapter 5 & Chapter 6 branches
  const branchesList = data?.branches || [];
  const chapter5 = branchesList.find((b) => b.id === 'chapter-5') || branchesList[0];
  const chapter6 = branchesList.find((b) => b.id === 'chapter-6') || branchesList[1];

  const floatingWhatsAppLink = createWhatsAppLink(
    data?.contactSettings?.whatsapp || '',
    `Halo ${data?.siteSettings?.brandName || 'LetonKu'}, saya ingin pesan kopi / info meja.`
  );

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] text-[#0F172A] selection:bg-[#0284C7] selection:text-white">
      <AnimatePresence mode="wait">
        {isPrivacyRoute ? (
          <motion.div
            key="privacy-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <PrivacyPolicyPage
              onBack={() => {
                setIsPrivacyRoute(false);
                try {
                  window.history.pushState(null, '', '/#home');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                } catch {
                  window.location.href = '/#home';
                }
              }}
            />
          </motion.div>
        ) : isStandaloneMode && isLoading ? (
          <PageSkeletonLoader key="loading-screen" onComplete={completeLoading} />
        ) : isAdminRoute ? (
          <motion.div
            key="admin-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {auth.isAuthenticated ? (
              auth.role === 'courier' ? (
                <CourierPortal />
              ) : (
                <AdminLayout onBackToPublic={closeAdmin} />
              )
            ) : (
              <AdminLogin onBackToPublic={closeAdmin} />
            )}
          </motion.div>
        ) : (showOnboarding && !customerProfile) ? (
          <PwaOnboarding
            key="pwa-onboarding"
            slides={data?.pwaOnboardingSlides}
            onComplete={() => {
              setOnboardingCompleted();
              setShowOnboarding(false);
              if (onboardingTargetTab === 'pesanan' || onboardingTargetTab === 'akun') {
                setActiveTab('home');
              }
              setOnboardingTargetTab(null);
            }}
            onLogin={() => {
              setOnboardingTargetTab((prev) => prev || 'home');
              setShowOnboarding(false);
              openMember();
            }}
          />
        ) : (
          <motion.div
            key="public-content"
            initial={{ opacity: isStandaloneMode ? 1 : 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: isStandaloneMode ? 0.2 : 0.5, ease: 'easeOut' }}
            className="fixed inset-0 w-screen h-[100dvh] overflow-hidden flex flex-col bg-[#F8FAFC] text-[#0F172A]"
          >
            {/* Public Navbar */}
            <Navbar
              onOpenMember={openMember}
              onOpenOrder={() => openOrdering()}
              activeTab={activeTab}
              onChangeTab={handleTabChange}
              customerProfile={customerProfile}
            />

            {/* Application Main Content Viewport (Scroll Container per Tab) */}
            <div className="flex-1 min-h-0 relative w-full overflow-hidden">
              {/* 1. HOME TAB CONTAINER */}
              {visitedTabs.has('home') && (
                <div
                  id="tab-container-home"
                  className="absolute inset-0 overflow-y-auto overflow-x-hidden scroll-smooth pb-28"
                  style={{ display: activeTab === 'home' ? 'block' : 'none' }}
                >
                  <div id="home">
                    <HeroSection
                      onOpenOrder={(item, initialType) => openOrdering(item, initialType)}
                      onOpenMember={openMember}
                      onNavigateTab={handleTabChange}
                      customerProfile={customerProfile}
                    />
                  </div>
                  <BaristasSection />
                  <ContactSection />
                </div>
              )}

              {/* 2. OUTLET TAB CONTAINER */}
              {visitedTabs.has('outlet') && (
                <div
                  id="tab-container-outlet"
                  className="absolute inset-0 overflow-y-auto overflow-x-hidden scroll-smooth pb-28 bg-[#F8F5EF]"
                  style={{ display: activeTab === 'outlet' ? 'block' : 'none' }}
                >
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2 font-body">
                    <div className="flex items-center gap-3 mb-2">
                      <button
                        type="button"
                        onClick={handleBackTab}
                        aria-label="Kembali"
                        title="Kembali"
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-[#B77945]/25 text-[#2B1D17] hover:bg-[#B77945]/10 transition-colors cursor-pointer shrink-0 shadow-2xs"
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-[#B77945] bg-[#B77945]/12 px-3 py-1 rounded-full border border-[#B77945]/20">
                        CHAPTER OUTLETS DUMAI
                      </span>
                    </div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-[#2B1D17] mt-2 tracking-[-0.02em]">
                      Kunjungi Outlet LetonKu
                    </h1>
                  </div>
                  {chapter5 && <ChapterSection branch={chapter5} reversed={false} />}
                  {chapter6 && <ChapterSection branch={chapter6} reversed={true} />}
                  <MobileTruckSection />
                  <OpenBoothSection />
                  <ContactSection />
                </div>
              )}

              {/* 3. PESANAN TAB CONTAINER */}
              {visitedTabs.has('pesanan') && (
                <div
                  id="tab-container-pesanan"
                  className="absolute inset-0 overflow-y-auto overflow-x-hidden scroll-smooth pb-32 bg-[#F8FBFF]"
                  style={{ display: activeTab === 'pesanan' ? 'block' : 'none' }}
                >
                  <div className="max-w-xl mx-auto px-4 py-6 space-y-4 font-sans text-[#172033]">
                    <div className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={handleBackTab}
                        aria-label="Kembali"
                        title="Kembali"
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-[#172033] hover:bg-slate-50 transition-colors cursor-pointer shrink-0 shadow-2xs"
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                      <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#0284C7] bg-[#F0F7FF] px-3 py-1 rounded-full border border-[#E0F2FE]">
                        Riwayat Belanja
                      </span>
                    </div>
                    <h1 className="font-display font-black text-2xl sm:text-3xl text-[#172033]">
                      Pesanan Saya
                    </h1>
                    <p className="text-xs text-slate-500">
                      Pantau status dan detail riwayat pesanan kopimu di sini.
                    </p>

                    {/* Search Input */}
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                      <input
                        type="text"
                        value={ordersSearchQuery}
                        onChange={(e) => setOrdersSearchQuery(e.target.value)}
                        placeholder="Cari nomor pesanan..."
                        className="w-full pl-10 pr-4 py-2 bg-white rounded-xl border border-slate-200 text-base sm:text-xs focus:border-[#0284C7] outline-none"
                      />
                    </div>

                    {/* Orders List */}
                    <div className="space-y-3">
                      {loadingOrders ? (
                        <div className="py-12 text-center text-slate-400 text-xs flex flex-col items-center gap-2">
                          <RefreshCw className="w-6 h-6 animate-spin text-[#0284C7]" />
                          <span>Memuat pesanan...</span>
                        </div>
                      ) : orders.length === 0 ? (
                        <div className="py-12 text-center text-slate-400 space-y-2 bg-white rounded-3xl border border-slate-200 p-6">
                          <Receipt className="w-10 h-10 mx-auto text-slate-300" />
                          <p className="text-xs">Belum ada riwayat pesanan.</p>
                        </div>
                      ) : (() => {
                        const filtered = orders.filter((o) => {
                          const num = (o.orderNumber || o.id || '').toLowerCase();
                          const query = ordersSearchQuery.toLowerCase().trim();
                          return !query || num.includes(query);
                        });

                        if (filtered.length === 0) {
                          return (
                            <div className="py-8 text-center text-slate-400 text-xs bg-white rounded-3xl border border-slate-200 p-6">
                              Tidak ada pesanan yang cocok dengan pencarian Anda.
                            </div>
                          );
                        }

                        return filtered.map((o) => {
                          const isExpanded = ordersExpandedId === o.id;
                          return (
                            <div
                              key={o.id}
                              className="p-4 bg-white rounded-2xl border border-slate-200 shadow-xs space-y-2.5 transition-all"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <span className="font-mono font-bold text-xs text-[#0284C7] block">
                                    {o.orderNumber}
                                  </span>
                                  <span className="text-[11px] text-slate-500 block mt-0.5">
                                    {formatOrderDateTime(o.createdAt)}
                                  </span>
                                </div>
                                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#F0F7FF] text-[#0284C7] border border-[#E0F2FE]">
                                  {o.orderStatus}
                                </span>
                              </div>

                              <div className="flex justify-between items-center text-xs pt-2 border-t border-slate-100">
                                <span className="text-slate-600 font-medium">Total Tagihan</span>
                                <span className="font-mono font-bold text-[#172033]">
                                  Rp {o.totalAmount.toLocaleString('id-ID')}
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={() => setOrdersExpandedId(isExpanded ? null : o.id)}
                                className="text-[11px] text-[#0284C7] font-bold flex items-center gap-1 hover:underline pt-1 cursor-pointer"
                              >
                                <span>{isExpanded ? 'Sembunyikan Item' : 'Lihat Detail Item'}</span>
                              </button>

                              {isExpanded && (
                                <div className="pt-2 border-t border-slate-100 space-y-1.5 text-xs text-slate-600 bg-slate-50 p-3 rounded-xl">
                                  {o.items.map((item, idx) => (
                                    <div key={idx} className="flex justify-between">
                                      <span>
                                        {item.quantity}x {item.name || 'Item'}
                                      </span>
                                      <span className="font-mono">
                                        Rp {((item.unitPrice || item.price || 0) * item.quantity).toLocaleString('id-ID')}
                                      </span>
                                    </div>
                                  ))}
                                  <div className="pt-1.5 mt-1.5 border-t border-slate-200/80 space-y-1">
                                    <div className="flex justify-between text-[11px] text-slate-500">
                                      <span>Subtotal Pesanan</span>
                                      <span className="font-mono">
                                        Rp {o.items.reduce((acc, it) => acc + ((it.unitPrice || it.price || 0) * (it.quantity || 1)), 0).toLocaleString('id-ID')}
                                      </span>
                                    </div>
                                    {Number(o.discountAmount || 0) > 0 && (
                                      <div className="flex justify-between text-[11px] text-emerald-600">
                                        <span>Diskon Voucher</span>
                                        <span className="font-mono">-Rp {Number(o.discountAmount || 0).toLocaleString('id-ID')}</span>
                                      </div>
                                    )}
                                    {o.orderType === 'DELIVERY' && Number(o.deliveryFee ?? o.delivery_fee ?? 0) > 0 && (
                                      <div className="flex justify-between text-[11px] text-slate-500">
                                        <span>Ongkos Kirim</span>
                                        <span className="font-mono">Rp {Number(o.deliveryFee ?? o.delivery_fee ?? 0).toLocaleString('id-ID')}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between text-[11px] text-slate-500">
                                      <span>Biaya Layanan</span>
                                      <span className="font-mono">Rp {resolvePersistedOrderServiceFee(o).toLocaleString('id-ID')}</span>
                                    </div>
                                    <div className="flex justify-between text-xs font-bold text-[#172033] pt-1 border-t border-slate-200/80">
                                      <span>Total Pembayaran</span>
                                      <span className="font-mono text-[#0284C7]">Rp {o.totalAmount.toLocaleString('id-ID')}</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* 4. MENU TAB CONTAINER */}
              {visitedTabs.has('menu') && (
                <div
                  id="tab-container-menu"
                  className="absolute inset-0 overflow-y-auto overflow-x-hidden scroll-smooth pb-28"
                  style={{ display: activeTab === 'menu' ? 'block' : 'none' }}
                >
                  <MenuSection onOpenOrder={(item) => openOrdering(item)} onBack={handleBackTab} />
                  <ContactSection />
                </div>
              )}

              {/* 4. AKUN TAB CONTAINER */}
              {visitedTabs.has('akun') && (
                <div
                  id="tab-container-akun"
                  className="absolute inset-0 overflow-y-auto overflow-x-hidden scroll-smooth pb-32 bg-[#F8FBFF]"
                  style={{ display: activeTab === 'akun' ? 'block' : 'none' }}
                >
                  {loadingCustomerProfile ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-slate-400">
                      <RefreshCw className="w-8 h-8 animate-spin text-[#0284C7]" />
                      <span className="text-xs font-mono">Memuat profil akun...</span>
                    </div>
                  ) : customerProfile ? (
                    <CustomerProfileTab
                      profile={customerProfile}
                      onBack={handleBackTab}
                      onStartOrder={() => {
                        handleTabChange('menu');
                      }}
                      onLogout={async () => {
                        try {
                          await logoutCustomer();
                        } catch (err) {
                          console.error('[Customer Logout Error]:', err);
                        } finally {
                          setCustomerProfile(null);
                          setActiveTab('home');
                        }
                      }}
                      onProfileUpdate={(updated) => {
                        setCustomerProfile((prev) => {
                          const next = prev ? { ...prev, ...updated } : null;
                          if (next) {
                            try {
                              localStorage.setItem('leton_customer_profile', JSON.stringify(next));
                            } catch {}
                          }
                          return next;
                        });
                      }}
                    />
                  ) : (
                    <div className="w-full min-h-screen sm:max-w-md sm:mx-auto sm:px-0 sm:py-0">
                      <CustomerAuthForm
                        onBackToChoice={handleBackTab}
                        onAuthSuccess={(profile) => {
                          setCustomerProfile(profile);
                          handleTabChange('home');
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fixed 4-Item Bottom Navigation */}
            {!isOrderingOpen && !isPwaInstallOpen && (
              <BottomNav
                activeTab={activeTab}
                onChangeTab={(tab) => {
                  handleTabChange(tab);
                }}
              />
            )}

            {/* Discreet Admin Lock Button (🔒) - hidden on Akun tab to avoid overlapping membership card */}
            {activeTab !== 'akun' && (
              <div className="fixed bottom-20 left-4 sm:bottom-5 sm:left-5 z-40">
                <button
                  onClick={openAdmin}
                  id="admin-lock-access-btn"
                  title="Portal Login Admin"
                  aria-label="Portal Login Admin"
                  className="p-3 rounded-full bg-slate-900/85 hover:bg-slate-900 text-slate-400 hover:text-[#00E5FF] border border-slate-700/60 hover:border-[#00E5FF]/60 shadow-xl backdrop-blur-md flex items-center justify-center transition-all transform hover:scale-110 active:scale-95 cursor-pointer group"
                >
                  <Lock className="w-4 h-4 transition-transform group-hover:rotate-12" />
                </button>
              </div>
            )}

            {/* Online Ordering System Modal */}
            <OrderingSystemModal
              isOpen={isOrderingOpen}
              onClose={closeOrdering}
              preSelectedMenuItem={orderingMenuItem}
              isMemberOnlyFlow={isMemberOnlyFlow}
              initialOutletId={deepLinkOutletId}
              initialOrderType={orderingInitialType}
            />

            {/* Minimal PWA Install Entry (?install=pwa) */}
            {isPwaInstallOpen && (
              <PwaInstallEntry
                onContinueToWebsite={continueToWebsite}
                onDismiss={closePwaInstall}
              />
            )}

            {/* PWA & Mobile Customer Notification Activation Prompt */}
            {!isAdminRoute && !isPwaInstallOpen && !isOrderingOpen && (
              <PwaNotificationPrompt />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function App() {
  return (
    <ErrorBoundary>
      <ContentProvider>
        <AppContent />
      </ContentProvider>
    </ErrorBoundary>
  );
}
